import React from 'react';
import type { UniversalAsset } from '@/types';
import { SpecialUserCard } from './SpecialUserCard';
import { SpecialAuctionCard } from './SpecialAuctionCard';
import { getUserKind, type UserKind } from '@/components/ui/UserTypeIcon';

// 5 Premium Dark Color Palettes
const DARK_PALETTES = [
    { bg: '#174d29', textColor: '#ffffff' }, // Dark Emerald
    { bg: '#1c1d22', textColor: '#fef08a' }, // Obsidian Gold
    { bg: '#0f243f', textColor: '#e0f2fe' }, // Midnight Cyan
    { bg: '#271138', textColor: '#fae8ff' }, // Velvet Purple
    { bg: '#25282e', textColor: '#f1f5f9' }, // Carbon
];

interface CardRendererProps {
    item?: UniversalAsset;
    onInteract?: () => void;
    onBuy?: () => void;
    onBid?: () => void;
    onOffer?: () => void;
    onClick?: () => void;
    context?: 'inventory' | 'market' | 'auction' | 'sold' | 'brand' | 'pending' | 'offer';
    scale?: number;
    compact?: boolean;
    grainOpacity?: number;
    isPending?: boolean;
    isOffer?: boolean;
    [key: string]: any;
}

function parseCardLabels(itemData: any) {
    let personName = '';
    let targetUsername = '';

    // 1. Extract Card Target Listed Username (Middle Box @)
    let rawUsername =
        itemData.verified_username ||
        itemData.card_username ||
        itemData.card?.username ||
        itemData.target_username ||
        itemData.listing_data?.username ||
        itemData.card_name ||
        itemData.card?.name ||
        itemData.displayName ||
        itemData.name ||
        itemData.username ||
        (itemData.serial ? `SERIAL_${itemData.serial}` : undefined) ||
        'CARD';

    if (typeof rawUsername === 'object' && rawUsername?.username) {
        rawUsername = rawUsername.username;
    }
    targetUsername = String(rawUsername).replace(/^@/, '').toUpperCase();

    // 2. Extract Person's First & Last Name (Top Header .boxes .name)
    const firstName =
        itemData.seller_first_name ||
        itemData.seller?.first_name ||
        itemData.owner?.first_name ||
        itemData.user?.first_name ||
        itemData.seller?.firstName ||
        itemData.owner?.firstName ||
        itemData.first_name;

    const lastName =
        itemData.seller_last_name ||
        itemData.seller?.last_name ||
        itemData.owner?.last_name ||
        itemData.user?.last_name ||
        itemData.seller?.lastName ||
        itemData.owner?.lastName ||
        itemData.last_name ||
        '';

    if (firstName && typeof firstName === 'string' && firstName.trim()) {
        personName = `${firstName.trim()} ${lastName ? String(lastName).trim() : ''}`.trim();
    } else {
        const explicitOwner =
            itemData.seller_name ||
            itemData.sellerName ||
            itemData.ownerName ||
            itemData.owner_name ||
            itemData.seller_username ||
            itemData.seller?.username ||
            itemData.owner?.username ||
            (typeof itemData.owner === 'string' ? itemData.owner : undefined) ||
            (typeof itemData.seller === 'string' ? itemData.seller : undefined);

        if (explicitOwner && typeof explicitOwner === 'string' && explicitOwner.trim()) {
            personName = explicitOwner.trim();
        } else {
            // Convert targetUsername into Title Case ("AHMED_VIP" -> "Ahmed Vip")
            personName = targetUsername
                .split('_')
                .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
                .join(' ');
        }
    }

    // Clean up personName: remove leading @
    personName = personName.replace(/^@/, '');

    return { personName, targetUsername };
}

export const CardRenderer: React.FC<CardRendererProps> = (props) => {
    // 1. Extract unified item data (handling both item object or flat props)
    const itemData = (props.item || props) as any;

    // 2. Extract Labels using robust parser
    const { personName, targetUsername } = parseCardLabels(itemData);

    // 3. Extract Avatar Image URL
    let avatarUrl =
        itemData.avatarUrl ||
        itemData.ownerAvatar ||
        itemData.ownerImage ||
        itemData.seller_avatar ||
        itemData.seller?.avatar ||
        itemData.owner_avatar ||
        itemData.profileImage ||
        itemData.image ||
        itemData.image_filename;

    if (!avatarUrl || typeof avatarUrl !== 'string' || avatarUrl.includes('placeholder')) {
        avatarUrl = '/icons/default-avatar.svg';
    } else if (avatarUrl.startsWith('card_Image/')) {
        avatarUrl = `/${avatarUrl}`;
    }

    // 4. Extract Price
    let priceVal: string | number =
        itemData.price ?? itemData.current_bid ?? itemData.startPrice ?? itemData.amount ?? '1,000';
    if (typeof priceVal === 'number') {
        priceVal = priceVal >= 1000 ? (priceVal / 1000).toFixed(1).replace(/\.0$/, '') + 'k' : priceVal.toLocaleString();
    }

    // 5. Strict 5 Dark Color Palettes Selection
    let rawBg =
        itemData.bg ||
        itemData.waveColor ||
        itemData.wave_color ||
        itemData.base_color ||
        itemData.color ||
        itemData.background_color;

    let selectedPalette = DARK_PALETTES.find(p => p.bg.toLowerCase() === String(rawBg).toLowerCase());
    if (!selectedPalette) {
        let hash = 0;
        const key = targetUsername + (itemData.id || personName);
        for (let i = 0; i < key.length; i++) hash += key.charCodeAt(i);
        selectedPalette = DARK_PALETTES[Math.abs(hash) % DARK_PALETTES.length];
    }

    const bg = selectedPalette.bg;
    const textColor = selectedPalette.textColor;
    const grainOpacity = props.grainOpacity ?? 0.30; // Strictly 30% grain density

    // 6. Interaction Handler
    const handleInteract = () => {
        if (props.onBuy) props.onBuy();
        else if (props.onBid) props.onBid();
        else if (props.onInteract) props.onInteract();
        else if (props.onClick) props.onClick();
        else if (itemData.onBuy) itemData.onBuy();
        else if (itemData.onClick) itemData.onClick();
    };

    // 7. Scale (Default: 0.6)
    const scale = props.scale || itemData.scale || 0.6;

    // 8. Check if this is an Auction Card
    const isAuction = Boolean(
        props.context === 'auction' ||
        itemData.type === 'AUCTION' ||
        itemData.isAuction ||
        itemData.isActiveAuction ||
        props.actionType === 'bid' ||
        itemData.auction_id ||
        itemData.auctionId ||
        (itemData.timeLeftSeconds !== undefined && itemData.timeLeftSeconds > 0)
    );

    // 9. Initial Seconds for Auction Timer
    let initialSeconds = 6 * 86400 + 5 * 3600 + 4 * 60; // 6D 5H 4M default
    if (itemData.timeLeftSeconds && typeof itemData.timeLeftSeconds === 'number') {
        initialSeconds = Math.max(0, itemData.timeLeftSeconds);
    } else if (itemData.timerHours && typeof itemData.timerHours === 'number') {
        initialSeconds = Math.max(0, Math.floor(itemData.timerHours * 3600));
    } else if (itemData.endTime || itemData.end_time) {
        const endMs = new Date(itemData.endTime || itemData.end_time).getTime();
        const nowMs = Date.now();
        if (endMs > nowMs) {
            initialSeconds = Math.floor((endMs - nowMs) / 1000);
        }
    }

    // 10. Bids Count
    const bidsCount = itemData.bidsCount ?? itemData.bids ?? itemData.totalBids ?? itemData.bidCount ?? itemData.bids_count ?? (itemData.stats?.bids) ?? 0;
    const hasBids = itemData.hasBids ?? (bidsCount > 0);

    // 11. User / Asset Kind (AI / P2P / NFT)
    const userKind: UserKind = getUserKind(itemData);

    // Render Auction Card or Standard User Card
    if (isAuction) {
        return (
            <SpecialAuctionCard
                displayName={personName}
                username={targetUsername}
                avatarUrl={avatarUrl}
                price={priceVal}
                bg={bg}
                textColor={textColor}
                grainOpacity={grainOpacity}
                initialSeconds={initialSeconds}
                scale={scale}
                compact={props.compact}
                userKind={userKind}
                isPending={itemData.isPending}
                hasBids={hasBids}
                bidsCount={bidsCount}
                onClick={handleInteract}
            />
        );
    }

    const isOffer = Boolean(
        props.context === 'offer' ||
        props.isOffer ||
        itemData.isOffer ||
        itemData.type === 'offer' ||
        itemData.type === 'OFFER' ||
        itemData.listing_type === 'offer' ||
        itemData.sale_type === 'offer'
    );

    const highestOfferVal = itemData.highestOffer ?? itemData.highest_offer ?? itemData.top_offer ?? itemData.currentOffer ?? itemData.topOfferAmt ?? props.highestOffer ?? props.highest_offer;

    return (
        <SpecialUserCard
            displayName={personName}
            username={targetUsername}
            avatarUrl={avatarUrl}
            price={priceVal}
            highestOffer={highestOfferVal}
            highest_offer={highestOfferVal}
            bg={bg}
            textColor={textColor}
            grainOpacity={grainOpacity}
            scale={scale}
            compact={props.compact}
            userKind={userKind}
            isOffer={isOffer}
            itemType={isOffer ? 'offer' : itemData.type}
            onClick={handleInteract}
            onOffer={props.onOffer || itemData.onOffer}
        />
    );
};

export default CardRenderer;
